import { type BreadcrumbItem, type Setting } from '@/types';
import { Head, router, usePage } from '@inertiajs/react';
import {
    AlertCircle,
    Briefcase,
    Building2,
    CheckCircle2,
    Clock,
    Database,
    Download,
    Image,
    RefreshCw,
    Settings,
    Shield,
    Ticket,
    Upload,
    Users,
    Wrench,
    XCircle,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import ImportProgressBar from '@/components/global/ImportProgressBar';
import BooleanEditor from '@/components/global/settings/BooleanEditor';
import CompanyCleanupEditor from '@/components/global/settings/CompanyCleanupEditor';
import CompanyDataEditor from '@/components/global/settings/CompanyDataEditor';
import CsvImportEditor from '@/components/global/settings/CsvImportEditor';
import JsonEditor from '@/components/global/settings/JsonEditor';
import LegacySqlImportEditor from '@/components/global/settings/LegacySqlImportEditor';
import NavigationVisibilityEditor from '@/components/global/settings/NavigationVisibilityEditor';
import NumberEditor from '@/components/global/settings/NumberEditor';
import SettingCard from '@/components/global/settings/SettingCard';
import UserDedupeEditor from '@/components/global/settings/UserDedupeEditor';
import { LocaleToggle } from '@/components/settings/LocaleToggle';
import { MultiSelectSettingDropdown } from '@/components/settings/MultiSelectSettingDropdown';
import ObjectFormSettingEditor from '@/components/settings/ObjectFormSettingEditor';
import { ProjectRoleTranslationsEditor } from '@/components/settings/ProjectRoleTranslationsEditor';
import { ProjectTaskDepartmentsEditor } from '@/components/settings/ProjectTaskDepartmentsEditor';
import { ProjectTaskTemplatesEditor } from '@/components/settings/ProjectTaskTemplatesEditor';
import RolePermissionsManager from '@/components/settings/RolePermissionsManager';
import { SearchableSettingDropdown } from '@/components/settings/SearchableSettingDropdown';
import { ServiceRequestTemplatesManager } from '@/components/settings/ServiceRequestTemplatesManager';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/shadcn/accordion';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Label } from '@/components/ui/shadcn/label';
import { Progress } from '@/components/ui/shadcn/progress';
import { useSettings } from '@/contexts/SettingsContext';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import SettingsLayout from '@/layouts/settings/layout';
import { csrfFetch } from '@/lib/csrfFetch';

interface SystemSettingsProps {
    settings: Record<string, Setting>;
    category?: string;
    pageTitle: string;
    categoryTitles: Record<string, string>;
    categoryDescriptions: Record<string, string>;
}

interface ActiveImport {
    id: string;
    type: 'companies_csv';
}

interface LastImportStatus {
    status: 'pending' | 'running' | 'completed' | 'failed';
    created_at?: string;
    completed_at?: string;
    started_at?: string;
    error?: string;
    message?: string;
    progress?: number;
}

export default function SystemSettings({ settings, category = 'general', pageTitle, categoryTitles, categoryDescriptions }: SystemSettingsProps) {
    const [activeImports, setActiveImports] = useState<ActiveImport[]>(() => {
        const stored = localStorage.getItem('active_imports');
        if (!stored) {
            return [];
        }
        try {
            const parsed: ActiveImport[] = JSON.parse(stored);
            return parsed.filter((imp) => imp.type === 'companies_csv');
        } catch {
            return [];
        }
    });
    const [lastImportStatuses, setLastImportStatuses] = useState<Record<string, LastImportStatus | null>>({});

    type MigrateTimelineStatus = 'idle' | 'running' | 'done' | 'error';
    interface MigrateTimelineError {
        project: string;
        message: string;
        action: string;
    }
    interface MigrateTimelineResult {
        converted: number;
        skipped: number;
        errors: MigrateTimelineError[];
    }
    const [migrateTimelineStatus, setMigrateTimelineStatus] = useState<MigrateTimelineStatus>('idle');
    const [migrateTimelineResult, setMigrateTimelineResult] = useState<MigrateTimelineResult | null>(null);
    const [migrateTimelineProgress, setMigrateTimelineProgress] = useState(0);

    const { flash } = usePage().props as { flash?: { success?: string; error?: string } };
    const { t } = useTranslations();
    const { settings: settingsConstants } = useSettings();

    // Handle flash messages
    useEffect(() => {
        if (flash?.success) {
            toast.success(flash.success);
        }
        if (flash?.error) {
            toast.error(flash.error);
        }
    }, [flash]);

    const handleSettingUpdate = (key: string, value: string | number | boolean | object | null) => {
        router.patch(
            route('settings.system.update', key),
            {
                value: value as any, // eslint-disable-line @typescript-eslint/no-explicit-any
            },
            {
                preserveScroll: true,
                onSuccess: () => {
                    // Success is handled by flash messages
                },
                onError: (errors) => {
                    if (errors.value) {
                        toast.error(t('settings.system.validation_error', 'Validation error: :error').replace(':error', errors.value));
                    } else {
                        toast.error(t('settings.system.failed_update_setting', 'Failed to update setting'));
                    }
                },
            },
        );
    };
    const rawHomeCountry = typeof settings.homeCountry?.value === 'string' ? settings.homeCountry.value : '';
    const homeCountryValue = rawHomeCountry && rawHomeCountry.length <= 3 ? rawHomeCountry.toUpperCase() : '';

    // Map category from URL to internal key
    const categoryMap: Record<string, string> = {
        general: 'general',
        navigation: 'navigation',
        companies: 'companies',
        employees: 'employees',
        crm: 'crm',
        projects: 'projects',
        'work-orders': 'workOrders',
        'data-import': 'dataImport',
        security: 'security',
        'service-requests': 'serviceRequests',
    };

    const activeCategoryKey = categoryMap[category] || 'general';

    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [uploadingFavicon, setUploadingFavicon] = useState(false);
    const logoInputRef = useRef<HTMLInputElement>(null);
    const faviconInputRef = useRef<HTMLInputElement>(null);

    const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setUploadingLogo(true);
        try {
            const formData = new FormData();
            formData.append('logo', file);

            const response = await csrfFetch('/settings/system/upload-logo', {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();

            if (data.success) {
                toast.success(data.message || t('settings.system.logo_uploaded', 'Logo uploaded successfully'));
                router.reload({ only: ['settings'], preserveUrl: true });
            } else {
                toast.error(data.message || t('settings.system.logo_upload_failed', 'Failed to upload logo'));
            }
        } catch (error) {
            toast.error(t('settings.system.logo_upload_failed', 'Failed to upload logo'));
            console.error('Error uploading logo:', error);
        } finally {
            setUploadingLogo(false);
            if (logoInputRef.current) {
                logoInputRef.current.value = '';
            }
        }
    };

    const handleFaviconUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setUploadingFavicon(true);
        try {
            const formData = new FormData();
            formData.append('favicon', file);

            const response = await csrfFetch('/settings/system/upload-favicon', {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();

            if (data.success) {
                toast.success(data.message || t('settings.system.favicon_uploaded', 'Favicon uploaded successfully'));
                router.reload({ only: ['settings'], preserveUrl: true });
            } else {
                toast.error(data.message || t('settings.system.favicon_upload_failed', 'Failed to upload favicon'));
            }
        } catch (error) {
            toast.error(t('settings.system.favicon_upload_failed', 'Failed to upload favicon'));
            console.error('Error uploading favicon:', error);
        } finally {
            setUploadingFavicon(false);
            if (faviconInputRef.current) {
                faviconInputRef.current.value = '';
            }
        }
    };

    const settingsByCategory = {
        general: {
            title: categoryTitles['general'] ?? t('settings.sections.general'),
            description: categoryDescriptions['general'] ?? '',
            icon: Settings,
            settings: ['countries', 'homeCountry', 'app.locale', 'notificationRetentionDays', 'defaultCompanyData'],
        },
        navigation: {
            title: categoryTitles['navigation'] ?? t('settings.sections.navigation', 'Navigation'),
            description: categoryDescriptions['navigation'] ?? '',
            icon: Settings,
            settings: ['navigationVisibility'],
        },
        companies: {
            title: categoryTitles['companies'] ?? t('settings.sections.companies', 'Companies'),
            description: categoryDescriptions['companies'] ?? '',
            icon: Building2,
            settings: ['companyCategories', 'companyTypes', 'companyPriorities'],
        },
        employees: {
            title: categoryTitles['employees'] ?? t('settings.sections.employees', 'Employees'),
            description: categoryDescriptions['employees'] ?? t('settings.section_descriptions_fallback.employees', 'Employee management settings'),
            icon: Users,
            settings: ['approvedEmailDomains'],
        },
        crm: {
            title: categoryTitles['crm'] ?? t('settings.sections.crm', 'CRM'),
            description: categoryDescriptions['crm'] ?? '',
            icon: Users,
            settings: [
                'leadStatuses',
                'leadPriorities',
                'leadSources',
                'leadOfferApprovalThreshold',
                'calculationRequestStatuses',
                'calculationRequestPhases',
                'calculationRequestComplexities',
            ],
        },
        projects: {
            title: categoryTitles['projects'] ?? t('settings.sections.projects', 'Projects'),
            description: categoryDescriptions['projects'] ?? '',
            icon: Briefcase,
            settings: ['projectStatuses', 'projectTaskStatuses', 'projectCountryTypes', 'projectTaskTemplates', 'projectCostLineTypes'],
        },
        workOrders: {
            title: categoryTitles['work-orders'] ?? t('settings.sections.work_orders', 'Work Orders'),
            description: categoryDescriptions['work-orders'] ?? '',
            icon: Wrench,
            settings: ['workOrderStatuses', 'workOrderPriorities', 'workOrderTypes', 'workOrderCostLineTypes'],
        },
        dataImport: {
            title: categoryTitles['data-import'] ?? t('settings.sections.data_import', 'Data Import'),
            description: categoryDescriptions['data-import'] ?? '',
            icon: Database,
            settings: [],
        },
        security: {
            title: categoryTitles['security'] ?? t('settings.sections.security', 'Security'),
            description: categoryDescriptions['security'] ?? '',
            icon: Shield,
            settings: [],
        },
        serviceRequests: {
            title: categoryTitles['service-requests'] ?? 'Service Requests',
            description: categoryDescriptions['service-requests'] ?? 'Manage service request templates.',
            icon: Ticket,
            settings: [],
        },
    };

    const currentCategory = settingsByCategory[activeCategoryKey as keyof typeof settingsByCategory];

    // Save active imports to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('active_imports', JSON.stringify(activeImports));
    }, [activeImports]);

    // Fetch last import statuses
    const fetchLastImportStatuses = async () => {
        try {
            const response = await fetch('/settings/system/last-import-status', {
                headers: {
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                credentials: 'same-origin',
            });

            if (response.ok) {
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    const data = await response.json();
                    setLastImportStatuses(data);
                } else {
                    // If response is not JSON, log the error
                    const text = await response.text();
                    console.error('Expected JSON but got:', contentType, text.substring(0, 200));
                    setLastImportStatuses({});
                }
            } else {
                // Handle non-200 responses
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    const error = await response.json();
                    console.error('Error fetching last import statuses:', error);
                } else {
                    console.error('Error fetching last import statuses: HTTP', response.status);
                }
                setLastImportStatuses({});
            }
        } catch (error) {
            console.error('Error fetching last import statuses:', error);
            setLastImportStatuses({});
        }
    };

    useEffect(() => {
        if (activeCategoryKey !== 'dataImport') {
            return undefined;
        }
        void fetchLastImportStatuses();
        const interval = setInterval(fetchLastImportStatuses, 10000);
        return () => clearInterval(interval);
    }, [activeCategoryKey]);

    const handleMigrateTimelineTasks = async () => {
        setMigrateTimelineStatus('running');
        setMigrateTimelineResult(null);
        setMigrateTimelineProgress(0);

        const progressInterval = setInterval(() => {
            setMigrateTimelineProgress((p) => Math.min(p + 8, 85));
        }, 300);

        try {
            const response = await csrfFetch('/settings/system/projects/migrate-timeline-tasks', {
                method: 'POST',
            });

            clearInterval(progressInterval);
            setMigrateTimelineProgress(100);

            if (!response.ok) {
                setMigrateTimelineStatus('error');
                toast.error(t('settings.system.migrate_timeline_tasks_failed', 'Migration failed. Please try again.'));
                return;
            }

            const data: MigrateTimelineResult = await response.json();
            setMigrateTimelineResult(data);
            setMigrateTimelineStatus('done');

            if (data.converted === 0 && data.skipped === 0) {
                toast.info(t('settings.system.migrate_timeline_tasks_nothing', 'No legacy timeline tasks found to migrate.'));
            } else {
                toast.success(
                    t('settings.system.migrate_timeline_tasks_success', 'Migration complete. :converted converted, :skipped skipped.')
                        .replace(':converted', String(data.converted))
                        .replace(':skipped', String(data.skipped)),
                );
            }
        } catch {
            clearInterval(progressInterval);
            setMigrateTimelineStatus('error');
            toast.error(t('settings.system.migrate_timeline_tasks_failed', 'Migration failed. Please try again.'));
        }
    };

    const handleCloseImport = (importJobId: string) => {
        setActiveImports((prev) => prev.filter((imp) => imp.id !== importJobId));
    };

    const handleCsvImportStart = (importJobId: string) => {
        setActiveImports((prev) => [...prev, { id: importJobId, type: 'companies_csv' }]);
        fetchLastImportStatuses(); // Refresh status
    };

    // Helper function to get status indicator
    const getStatusIndicator = (status: LastImportStatus | null) => {
        if (!status) {
            return { icon: Clock, color: 'text-muted-foreground', bgColor: 'bg-muted', label: t('settings.system.status_never_run', 'Never run') };
        }

        switch (status.status) {
            case 'completed':
                return {
                    icon: CheckCircle2,
                    color: 'text-success-subtle-foreground',
                    bgColor: 'bg-success-subtle',
                    label: t('settings.system.status_success', 'Success'),
                };
            case 'failed':
                return { icon: XCircle, color: 'text-destructive', bgColor: 'bg-danger-subtle', label: t('settings.system.status_failed', 'Failed') };
            case 'running':
                return {
                    icon: Clock,
                    color: 'text-info-subtle-foreground',
                    bgColor: 'bg-info-subtle',
                    label: t('settings.system.status_running', 'Running'),
                };
            case 'pending':
                return {
                    icon: AlertCircle,
                    color: 'text-warning-subtle-foreground',
                    bgColor: 'bg-warning-subtle',
                    label: t('settings.system.status_pending', 'Pending'),
                };
            default:
                return { icon: Clock, color: 'text-muted-foreground', bgColor: 'bg-muted', label: t('settings.system.status_unknown', 'Unknown') };
        }
    };

    // Helper function to format last import time
    const formatLastImportTime = (status: LastImportStatus | null) => {
        if (!status) return t('settings.system.time_never', 'Never');

        const time = status.completed_at || status.started_at;
        if (!time) return t('settings.system.time_never', 'Never');

        const date = new Date(time);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return t('settings.system.time_just_now', 'Just now');
        if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
        if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const renderSettingEditor = (settingKey: string, setting: Setting) => {
        // Determine if setting is single select dropdown (object arrays with value/label)
        const isSingleSelect = (key: string) => {
            return [
                'companyPriorities',
                'leadStatuses',
                'leadPriorities',
                'workOrderStatuses',
                'workOrderPriorities',
                'projectStatuses',
                'projectTaskStatuses',
                'projectCountryTypes',
                'workOrderTypes',
                'calculationRequestStatuses',
                'calculationRequestPhases',
                'calculationRequestComplexities',
                'homeCountry',
            ].includes(key);
        };

        const getSettingItemValue = (item: unknown): string => {
            if (typeof item === 'string') {
                return item;
            }
            if (typeof item === 'object' && item !== null) {
                if ('value' in item) {
                    return String((item as { value: unknown }).value);
                }
            }
            return String(item);
        };

        const buildMultiSelectValue = (currentValue: unknown[], selected: string[]) => {
            const existingValues = currentValue.map(getSettingItemValue);
            const isObjectArray = currentValue.some((item) => typeof item === 'object' && item !== null);
            const preservedItems = currentValue.filter((item) => selected.includes(getSettingItemValue(item)));
            const newItems = selected
                .filter((value) => !existingValues.includes(value))
                .map((value) => (isObjectArray ? { value, label: value } : value));

            return [...preservedItems, ...newItems];
        };

        switch (setting.editable_mode) {
            case 'company_data_editor':
                return <CompanyDataEditor setting={setting} onUpdate={handleSettingUpdate} />;
            case 'boolean_editor':
                return <BooleanEditor setting={setting} onUpdate={handleSettingUpdate} />;
            case 'navigation_visibility':
                return <NavigationVisibilityEditor setting={setting} onUpdate={handleSettingUpdate} />;
            case 'number_editor': {
                // Bounds mirror the backend rule for each key: a percentage for
                // the approval threshold, whole days for notification retention.
                const bounds = settingKey === 'notificationRetentionDays' ? { min: 1, max: 3650, step: 1 } : { min: 0, max: 100, step: 0.1 };

                return <NumberEditor setting={setting} onUpdate={(key, value) => handleSettingUpdate(key, value)} {...bounds} />;
            }
            case 'language_select':
                return (
                    <div className="space-y-2">
                        <LocaleToggle value={(setting.value as string) || 'en'} onUpdate={(value) => handleSettingUpdate(settingKey, value)} />
                        <p className="text-muted-foreground text-sm">
                            {t('settings.system.locale_description', 'Select the default language for the application interface.')}
                        </p>
                    </div>
                );
            case 'single_select':
                if (settingKey === 'homeCountry') {
                    return (
                        <div className="space-y-2">
                            <Label>{t('settings.system.home_country_label', 'Home Country')}</Label>
                            <SearchableSettingDropdown
                                settingKey={settingKey}
                                value={(setting.value as string) || undefined}
                                onValueChange={(value) => handleSettingUpdate(settingKey, value)}
                                setting={setting}
                                placeholder={t('settings.system.home_country_placeholder', 'Select home country')}
                            />
                            <p className="text-muted-foreground text-sm">
                                {t('settings.system.home_country_description', 'The default country for new companies and addresses.')}
                            </p>
                        </div>
                    );
                }
                // For other single selects (object arrays), use SearchableSettingDropdown
                if (isSingleSelect(settingKey)) {
                    // Get current value - for object arrays, find the selected one
                    const currentValue =
                        Array.isArray(setting.value) && setting.value.length > 0
                            ? typeof setting.value[0] === 'object' && setting.value[0] !== null && 'value' in setting.value[0]
                                ? setting.value[0].value
                                : String(setting.value[0])
                            : undefined;

                    return (
                        <div className="space-y-2">
                            <Label>{formatSettingName(settingKey)}</Label>
                            <SearchableSettingDropdown
                                settingKey={settingKey}
                                value={currentValue}
                                onValueChange={(value) => {
                                    // For now, just update with the value - the backend will handle the structure
                                    // In a full implementation, you'd want to maintain the object structure
                                    handleSettingUpdate(settingKey, value);
                                }}
                                setting={setting}
                                placeholder={`Select ${formatSettingName(settingKey)}`}
                            />
                        </div>
                    );
                }
                return (
                    <SearchableSettingDropdown
                        settingKey={settingKey}
                        value={(setting.value as string) || undefined}
                        onValueChange={(value) => handleSettingUpdate(settingKey, value)}
                        setting={setting}
                        placeholder={`Select ${formatSettingName(settingKey)}`}
                    />
                );
            case 'tag_input': {
                const currentValue = Array.isArray(setting.value) ? setting.value : [];
                const selectedValues = currentValue.map(getSettingItemValue);

                return (
                    <div className="space-y-2">
                        <Label>{formatSettingName(settingKey)}</Label>
                        <MultiSelectSettingDropdown
                            settingKey={settingKey}
                            selected={selectedValues}
                            onChange={(selected) => {
                                const newValue = buildMultiSelectValue(currentValue, selected);
                                handleSettingUpdate(settingKey, newValue);
                            }}
                            setting={setting}
                            placeholder={`Select ${formatSettingName(settingKey)}`}
                        />
                    </div>
                );
            }
            case 'object_form':
                // Object forms (statuses/priorities with value/label/color) use StatusEditor
                // for add/edit/delete, wrapped so admins can also author the per-language
                // labels — StatusEditor itself only edits the English label.
                return <ObjectFormSettingEditor setting={setting} onUpdate={handleSettingUpdate} />;
            case 'templates':
                return (
                    <ProjectTaskTemplatesEditor
                        settingKey={settingKey}
                        setting={setting}
                        projectTaskDepartments={
                            settings.projectTaskDepartments?.value as unknown as Array<{ value: string; label?: string; roles?: string[] }>
                        }
                        availableRoles={settingsConstants.availableRoles ?? []}
                        onUpdate={handleSettingUpdate}
                    />
                );
            case 'task_departments':
                return (
                    <ProjectTaskDepartmentsEditor
                        settingKey={settingKey}
                        setting={setting}
                        availableRoles={settingsConstants.availableRoles ?? []}
                        onUpdate={handleSettingUpdate}
                    />
                );
            case 'role_translations':
                return (
                    <ProjectRoleTranslationsEditor
                        settingKey={settingKey}
                        setting={setting}
                        availableRoles={settingsConstants.availableRoles ?? []}
                        projectTaskDepartments={Array.isArray(settings.projectTaskDepartments?.value) ? settings.projectTaskDepartments.value : []}
                        onUpdate={handleSettingUpdate}
                    />
                );
            case 'json_editor':
            default:
                return <JsonEditor setting={setting} onUpdate={handleSettingUpdate} />;
        }
    };

    const formatSettingName = (key: string) => {
        return key
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, (str) => str.toUpperCase())
            .trim();
    };

    const getSettingDescription = (key: string): string | undefined => {
        const descriptions: Record<string, string> = {
            countries: t(
                'settings.setting_descriptions.countries',
                'Add countries that will be available throughout the system. Each country requires a value (ISO code) and display label.',
            ),
            homeCountry: t('settings.setting_descriptions.home_country', 'The default country used for new companies and addresses.'),
            'app.locale': t('settings.setting_descriptions.app_locale', 'The default language for the application interface.'),
            approvedEmailDomains: t(
                'settings.setting_descriptions.approved_email_domains',
                'Email domains that are allowed for employee accounts. Only users with email addresses from these domains can be created.',
            ),
            defaultCompanyData: t('settings.setting_descriptions.default_company_data', 'Default values used when creating new companies.'),
            projectTaskTemplates: t(
                'settings.setting_descriptions.project_task_templates',
                'Templates used to create default Gantt tasks when a project is created, or when applying a template later.',
            ),
            projectTaskDepartments: t(
                'settings.setting_descriptions.project_task_departments',
                'Departments define which role groups are allowed to edit project tasks.',
            ),
            projectRoleTranslations: t(
                'settings.setting_descriptions.project_role_translations',
                'Set Lithuanian labels for role names displayed in department and task interfaces.',
            ),
            leadOfferApprovalThreshold: t(
                'settings.setting_descriptions.lead_offer_approval_threshold',
                'Offers with a profit margin at or above this percentage are approved automatically; anything below is sent for sales approval.',
            ),
            notificationRetentionDays: t(
                'settings.setting_descriptions.notification_retention_days',
                'How many days a read notification is kept before it is deleted overnight. Unread notifications are never removed automatically.',
            ),
        };
        return descriptions[key];
    };

    const renderSettingCards = (settingKeys: string[]) => {
        return (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {settingKeys.map((settingKey) => {
                    const setting = settings[settingKey];
                    if (!setting) {
                        return null;
                    }

                    return (
                        <SettingCard key={settingKey} settingKey={settingKey} setting={setting} description={getSettingDescription(settingKey)}>
                            {renderSettingEditor(settingKey, setting)}
                        </SettingCard>
                    );
                })}
            </div>
        );
    };

    const settingsSectionsByCategory = {
        general: [
            {
                id: 'localization',
                title: t('settings.subsections.localization', 'Localization'),
                description: t('settings.subsections.localization_description', 'Language and country defaults.'),
                settings: ['app.locale', 'homeCountry', 'countries'],
            },
            {
                id: 'projects',
                title: t('settings.subsections.projects', 'Projects'),
                description: t('settings.subsections.projects_description', 'Project creation and defaults.'),
                settings: [],
            },
            {
                id: 'company-defaults',
                title: t('settings.subsections.company_defaults', 'Company defaults'),
                description: t('settings.subsections.company_defaults_description', 'Default values used for new companies.'),
                settings: ['defaultCompanyData'],
            },
            {
                id: 'notifications',
                title: t('settings.subsections.notifications', 'Notifications'),
                description: t('settings.subsections.notifications_description', 'How long notifications are kept.'),
                settings: ['notificationRetentionDays'],
            },
        ],
        navigation: [
            {
                id: 'visibility',
                title: t('settings.subsections.visibility', 'Navigation visibility'),
                description: t('settings.subsections.visibility_description', 'Choose what appears in the main navigation.'),
                settings: ['navigationVisibility'],
            },
        ],
        companies: [
            {
                id: 'classification',
                title: t('settings.subsections.classification', 'Classification'),
                description: t('settings.subsections.classification_description', 'Types and categories used across company forms.'),
                settings: ['companyTypes', 'companyCategories'],
            },
            {
                id: 'priorities',
                title: t('settings.subsections.priorities', 'Priorities'),
                description: t('settings.subsections.priorities_description', 'Priority options for companies.'),
                settings: ['companyPriorities'],
            },
        ],
        employees: [
            {
                id: 'email-domains',
                title: t('settings.subsections.email_domains', 'Email domain allowlist'),
                description: t('settings.subsections.email_domains_description', 'Restrict employee accounts by email domain.'),
                settings: ['approvedEmailDomains'],
            },
        ],
        crm: [
            {
                id: 'leads',
                title: t('settings.subsections.leads', 'Leads'),
                description: t('settings.subsections.leads_description', 'Lead statuses, priorities, and sources.'),
                settings: ['leadStatuses', 'leadPriorities', 'leadSources', 'leadOfferApprovalThreshold'],
            },
            {
                id: 'calculation-requests',
                title: t('settings.subsections.calculation_requests', 'Calculation requests'),
                description: t(
                    'settings.subsections.calculation_requests_description',
                    'Statuses, project phases and complexity for calculation requests. Each status must be assigned a lifecycle stage — that is what the queue and the reporting use, so renaming or adding a status is safe.',
                ),
                settings: ['calculationRequestStatuses', 'calculationRequestPhases', 'calculationRequestComplexities'],
            },
        ],
        projects: [
            {
                id: 'statuses',
                title: t('settings.subsections.statuses', 'Statuses'),
                description: t('settings.subsections.statuses_description', 'Project and task workflow statuses.'),
                settings: ['projectStatuses', 'projectTaskStatuses'],
            },
            {
                id: 'task-config',
                title: t('settings.subsections.task_config', 'Task configuration'),
                description: t('settings.subsections.task_config_description', 'Task departments and role labels used by the Gantt.'),
                settings: ['projectTaskDepartments', 'projectRoleTranslations'],
            },
            {
                id: 'templates',
                title: t('settings.subsections.templates', 'Templates'),
                description: t('settings.subsections.templates_description', 'Create reusable task templates for new projects.'),
                settings: ['projectTaskTemplates'],
            },
            {
                id: 'project-fields',
                title: t('settings.subsections.project_fields', 'Project fields'),
                description: t('settings.subsections.project_fields_description', 'Enums and dropdown values used in project forms.'),
                settings: ['projectCountryTypes'],
            },
            {
                id: 'costs',
                title: t('settings.subsections.costs', 'Costs'),
                description: t('settings.subsections.costs_description_projects', 'Cost line types available for project costs.'),
                settings: ['projectCostLineTypes'],
            },
        ],
        workOrders: [
            {
                id: 'workflow',
                title: t('settings.subsections.workflow', 'Workflow'),
                description: t('settings.subsections.workflow_description_work_orders', 'Work order statuses, priorities, and types.'),
                settings: ['workOrderStatuses', 'workOrderPriorities', 'workOrderTypes'],
            },
            {
                id: 'costs',
                title: t('settings.subsections.costs', 'Costs'),
                description: t('settings.subsections.costs_description_work_orders', 'Cost line types available for work orders.'),
                settings: ['workOrderCostLineTypes'],
            },
        ],
    } satisfies Record<
        string,
        Array<{
            id: string;
            title: string;
            description?: string;
            settings: string[];
        }>
    >;

    const renderSectionedSettings = (categoryKey: string, fallbackKeys: string[]) => {
        const sections =
            (settingsSectionsByCategory as Record<string, (typeof settingsSectionsByCategory)[keyof typeof settingsSectionsByCategory] | undefined>)[
                categoryKey
            ] ?? null;
        if (!sections) {
            return renderSettingCards(fallbackKeys);
        }

        const visibleSections = sections
            .map((section) => ({
                ...section,
                settings: section.settings.filter((key) => Boolean(settings[key])),
            }))
            .filter((section) => section.settings.length > 0);

        if (visibleSections.length === 0) {
            return renderSettingCards(fallbackKeys);
        }

        return (
            <Accordion type="multiple" className="rounded-md border">
                {visibleSections.map((section) => (
                    <AccordionItem key={section.id} value={section.id} className="px-4">
                        <AccordionTrigger className="hover:no-underline">
                            <div className="flex flex-col items-start gap-1 text-left">
                                <div className="text-sm font-semibold">{section.title}</div>
                                {section.description ? <div className="text-muted-foreground text-xs">{section.description}</div> : null}
                            </div>
                        </AccordionTrigger>
                        <AccordionContent className="pt-2">{renderSettingCards(section.settings)}</AccordionContent>
                    </AccordionItem>
                ))}
            </Accordion>
        );
    };

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: pageTitle,
            href: '/settings/system/general',
        },
        ...(category && categoryTitles[category]
            ? [
                  {
                      title: categoryTitles[category],
                      href: `/settings/system/${category}`,
                  },
              ]
            : []),
    ];

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title={pageTitle} />

            <SettingsLayout>
                <div className="space-y-6">
                    {currentCategory && (
                        <>
                            <div className="space-y-1">
                                <h1 className="text-xl font-semibold tracking-tight">{currentCategory.title}</h1>
                                {currentCategory.description && <p className="text-muted-foreground text-sm">{currentCategory.description}</p>}
                            </div>

                            <div className="space-y-4">
                                {currentCategory.settings.length === 0 &&
                                activeCategoryKey !== 'security' &&
                                activeCategoryKey !== 'dataImport' &&
                                activeCategoryKey !== 'serviceRequests' ? (
                                    <Card>
                                        <CardContent className="text-muted-foreground py-8 text-center">
                                            {t('settings.system.no_settings', 'No settings available for this category.')}
                                        </CardContent>
                                    </Card>
                                ) : (
                                    <>{renderSectionedSettings(activeCategoryKey, currentCategory.settings)}</>
                                )}

                                {/* Logo and Favicon Upload - General Category Only */}
                                {activeCategoryKey === 'general' && (
                                    <>
                                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                            <Card>
                                                <CardHeader>
                                                    <div className="flex items-center gap-2">
                                                        <Image className="h-5 w-5" />
                                                        <CardTitle className="text-base">
                                                            {t('settings.system.app_logo', 'Application Logo')}
                                                        </CardTitle>
                                                    </div>
                                                    <CardDescription className="text-xs">
                                                        {t(
                                                            'settings.system.app_logo_description',
                                                            'Upload a logo image (SVG, PNG, JPG, JPEG, WEBP). Max 2MB.',
                                                        )}
                                                    </CardDescription>
                                                </CardHeader>
                                                <CardContent className="space-y-4">
                                                    {settings['app.logo']?.value && (
                                                        <div className="bg-muted flex items-center justify-center rounded-lg p-4">
                                                            <img
                                                                src={`/storage/${settings['app.logo'].value}`}
                                                                alt="Current logo"
                                                                className="max-h-20 max-w-full object-contain"
                                                            />
                                                        </div>
                                                    )}
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            ref={logoInputRef}
                                                            type="file"
                                                            accept=".svg,.png,.jpg,.jpeg,.webp"
                                                            onChange={handleLogoUpload}
                                                            disabled={uploadingLogo}
                                                            className="hidden"
                                                            id="logo-upload"
                                                        />
                                                        <Button
                                                            onClick={() => logoInputRef.current?.click()}
                                                            disabled={uploadingLogo}
                                                            className="flex-1"
                                                            size="sm"
                                                            variant="outline"
                                                        >
                                                            <Upload className="mr-2 h-4 w-4" />
                                                            {uploadingLogo
                                                                ? t('settings.system.uploading', 'Uploading...')
                                                                : t('settings.system.upload_logo', 'Upload Logo')}
                                                        </Button>
                                                    </div>
                                                </CardContent>
                                            </Card>

                                            <Card>
                                                <CardHeader>
                                                    <div className="flex items-center gap-2">
                                                        <Image className="h-5 w-5" />
                                                        <CardTitle className="text-base">{t('settings.system.favicon', 'Favicon')}</CardTitle>
                                                    </div>
                                                    <CardDescription className="text-xs">
                                                        {t(
                                                            'settings.system.favicon_description',
                                                            'Upload a favicon image (SVG, PNG, JPG, JPEG, WEBP, ICO). Max 500KB.',
                                                        )}
                                                    </CardDescription>
                                                </CardHeader>
                                                <CardContent className="space-y-4">
                                                    {settings['app.favicon']?.value && (
                                                        <div className="bg-muted flex items-center justify-center rounded-lg p-4">
                                                            <img
                                                                src={`/storage/${settings['app.favicon'].value}`}
                                                                alt="Current favicon"
                                                                className="h-16 w-16 object-contain"
                                                            />
                                                        </div>
                                                    )}
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            ref={faviconInputRef}
                                                            type="file"
                                                            accept=".svg,.png,.jpg,.jpeg,.webp,.ico"
                                                            onChange={handleFaviconUpload}
                                                            disabled={uploadingFavicon}
                                                            className="hidden"
                                                            id="favicon-upload"
                                                        />
                                                        <Button
                                                            onClick={() => faviconInputRef.current?.click()}
                                                            disabled={uploadingFavicon}
                                                            className="flex-1"
                                                            size="sm"
                                                            variant="outline"
                                                        >
                                                            <Upload className="mr-2 h-4 w-4" />
                                                            {uploadingFavicon
                                                                ? t('settings.system.uploading', 'Uploading...')
                                                                : t('settings.system.upload_favicon', 'Upload Favicon')}
                                                        </Button>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        </div>
                                    </>
                                )}

                                {/* Data Import Actions */}
                                {activeCategoryKey === 'dataImport' && (
                                    <div className="space-y-4">
                                        <CompanyCleanupEditor />
                                        <UserDedupeEditor />
                                        <Card className="border-warning-subtle-foreground/30">
                                            <CardHeader>
                                                <div className="flex items-center gap-2">
                                                    <Database className="text-warning-subtle-foreground h-5 w-5" />
                                                    <CardTitle className="text-base">Legacy SQL Import</CardTitle>
                                                </div>
                                                <CardDescription className="text-xs">
                                                    Import legacy users, companies, projects, and tasks from a SQL dump file. Runs the{' '}
                                                    <code className="bg-muted rounded px-1">migrate:legacy-projects</code> command in the background.
                                                </CardDescription>
                                            </CardHeader>
                                            <CardContent>
                                                <LegacySqlImportEditor />
                                            </CardContent>
                                        </Card>
                                        {(() => {
                                            const status = lastImportStatuses.companies_csv;
                                            const indicator = getStatusIndicator(status);
                                            const StatusIcon = indicator.icon;
                                            return (
                                                <Card>
                                                    <CardHeader>
                                                        <div className="flex items-start justify-between">
                                                            <div className="flex items-center gap-2">
                                                                <Download className="h-5 w-5" />
                                                                <CardTitle className="text-base">
                                                                    {t('settings.system.import_csv_title', 'Import Companies from CSV')}
                                                                </CardTitle>
                                                            </div>
                                                            <div className={`${indicator.bgColor} ${indicator.color} rounded-full p-1.5`}>
                                                                <StatusIcon className="h-4 w-4" />
                                                            </div>
                                                        </div>
                                                        <CardDescription className="text-xs">
                                                            {t(
                                                                'settings.system.import_csv_description',
                                                                'Upload CSV exports (e.g., ClientsMarx) with optional auto-enrichment',
                                                            )}
                                                        </CardDescription>
                                                    </CardHeader>
                                                    <CardContent className="space-y-4">
                                                        <div className="space-y-1 text-xs">
                                                            <div className="text-muted-foreground flex items-center justify-between">
                                                                <span>{t('settings.system.last_run', 'Last run:')}</span>
                                                                <span className="font-medium">{formatLastImportTime(status)}</span>
                                                            </div>
                                                            {status?.error && (
                                                                <div className="bg-danger-subtle text-danger-subtle-foreground mt-2 rounded p-2 text-xs">
                                                                    <div className="flex items-start gap-1">
                                                                        <AlertCircle className="mt-0.5 h-3 w-3 flex-shrink-0" />
                                                                        <span className="line-clamp-2">{status.error}</span>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <CsvImportEditor onImportStart={handleCsvImportStart} defaultCountry={homeCountryValue} />
                                                    </CardContent>
                                                </Card>
                                            );
                                        })()}
                                        <Card>
                                            <CardHeader>
                                                <div className="flex items-center gap-2">
                                                    <RefreshCw className="h-5 w-5" />
                                                    <CardTitle className="text-base">
                                                        {t('settings.system.migrate_timeline_tasks_title', 'Migrate Legacy Timeline Tasks')}
                                                    </CardTitle>
                                                </div>
                                                <CardDescription className="text-xs">
                                                    {t(
                                                        'settings.system.migrate_timeline_tasks_description',
                                                        'Convert imported tasks named "Pradžia", "Pabaiga", or type "start_finish" into proper project timeline tasks. Start/end dates are preserved.',
                                                    )}
                                                </CardDescription>
                                            </CardHeader>
                                            <CardContent className="space-y-4">
                                                {migrateTimelineStatus === 'running' && (
                                                    <div className="space-y-2">
                                                        <Progress value={migrateTimelineProgress} className="h-2" />
                                                        <p className="text-muted-foreground text-xs">
                                                            {t('settings.system.migrate_timeline_tasks_running', 'Migrating...')}
                                                        </p>
                                                    </div>
                                                )}
                                                {migrateTimelineStatus === 'done' && migrateTimelineResult && (
                                                    <div className="space-y-2">
                                                        <Progress value={100} className="h-2" />
                                                        <div className="flex flex-wrap gap-4 text-sm">
                                                            <span className="text-success-subtle-foreground flex items-center gap-1">
                                                                <CheckCircle2 className="h-4 w-4" />
                                                                {t('settings.system.migrate_timeline_tasks_converted', 'Converted: :count').replace(
                                                                    ':count',
                                                                    String(migrateTimelineResult.converted),
                                                                )}
                                                            </span>
                                                            <span className="text-muted-foreground flex items-center gap-1">
                                                                <Clock className="h-4 w-4" />
                                                                {t(
                                                                    'settings.system.migrate_timeline_tasks_skipped',
                                                                    'Skipped (already had timeline): :count',
                                                                ).replace(':count', String(migrateTimelineResult.skipped))}
                                                            </span>
                                                            {migrateTimelineResult.errors.length > 0 && (
                                                                <span className="text-destructive flex items-center gap-1">
                                                                    <XCircle className="h-4 w-4" />
                                                                    {t('settings.system.migrate_timeline_tasks_errors', 'Errors: :count').replace(
                                                                        ':count',
                                                                        String(migrateTimelineResult.errors.length),
                                                                    )}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {migrateTimelineResult.errors.length > 0 && (
                                                            <ul className="mt-2 space-y-2">
                                                                {migrateTimelineResult.errors.map((err, i) => (
                                                                    <li
                                                                        key={i}
                                                                        className="bg-danger-subtle text-danger-subtle-foreground rounded-md p-3 text-xs"
                                                                    >
                                                                        <div className="flex items-start gap-2">
                                                                            <AlertCircle className="text-destructive mt-0.5 h-3.5 w-3.5 shrink-0" />
                                                                            <div className="space-y-0.5">
                                                                                <p className="text-danger-subtle-foreground font-medium">
                                                                                    {err.project}
                                                                                </p>
                                                                                <p className="text-danger-subtle-foreground">{err.message}</p>
                                                                                <p className="text-muted-foreground">{err.action}</p>
                                                                            </div>
                                                                        </div>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                    </div>
                                                )}
                                                {migrateTimelineStatus === 'error' && (
                                                    <div className="text-destructive flex items-center gap-2 text-sm">
                                                        <XCircle className="h-4 w-4" />
                                                        {t('settings.system.migrate_timeline_tasks_failed', 'Migration failed. Please try again.')}
                                                    </div>
                                                )}
                                                <Button
                                                    onClick={() => void handleMigrateTimelineTasks()}
                                                    disabled={migrateTimelineStatus === 'running'}
                                                    size="sm"
                                                    variant="outline"
                                                >
                                                    <RefreshCw
                                                        className={`mr-2 h-4 w-4 ${migrateTimelineStatus === 'running' ? 'animate-spin' : ''}`}
                                                    />
                                                    {migrateTimelineStatus === 'running'
                                                        ? t('settings.system.migrate_timeline_tasks_running', 'Migrating...')
                                                        : t('settings.system.migrate_timeline_tasks_button', 'Run Migration')}
                                                </Button>
                                            </CardContent>
                                        </Card>
                                    </div>
                                )}
                                {activeCategoryKey === 'security' && <RolePermissionsManager />}
                                {activeCategoryKey === 'serviceRequests' && <ServiceRequestTemplatesManager />}
                            </div>
                        </>
                    )}
                </div>

                {/* Progress Bars for Active Imports */}
                {activeImports.map((activeImport, index) => (
                    <ImportProgressBar
                        key={activeImport.id}
                        importJobId={activeImport.id}
                        type={activeImport.type}
                        onClose={() => handleCloseImport(activeImport.id)}
                        index={activeImports.length - 1 - index}
                    />
                ))}
            </SettingsLayout>
        </AppLayout>
    );
}
